/* This small, independent controller runs directly after the navigation markup.
   Optional product/recommendation modules cannot delay or disable its links. */
(() => {
  if (!window.customElements || customElements.get('bd-navigation')) return;

  class BDNavigation extends HTMLElement {
    connectedCallback() {
      // A later parser-created instance may connect before its children exist.
      // The first instance is already complete when this script defines it.
      if (!this.querySelector('.bd-category-menu > summary') && document.readyState === 'loading') {
        this.onParsed = () => this.initialize();
        document.addEventListener('DOMContentLoaded', this.onParsed, { once: true });
        return;
      }
      this.initialize();
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.menu = this.querySelector('.bd-category-menu');
      this.summary = this.menu?.querySelector('summary');
      if (!this.summary) return;
      this.initialized = true;
      this.rail = this.querySelector('[data-category-shortcuts]');
      this.links = this.rail ? [...this.rail.querySelectorAll('a')] : [];
      this.onFocusIn = event => {
        this.lastFocused = this.links.includes(event.target) ? event.target : null;
      };
      this.onKeyDown = event => {
        if (event.key !== 'Escape' || !this.menu.open) return;
        this.menu.open = false;
        this.summary.focus({ preventScroll: true });
      };
      this.onDocumentClick = event => {
        if (this.menu.open && !this.menu.contains(event.target)) this.menu.open = false;
      };
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('click', this.onDocumentClick);

      if (!this.rail) return;
      this.scheduleFit = () => {
        cancelAnimationFrame(this.fitFrame);
        this.fitFrame = requestAnimationFrame(() => this.fit());
      };
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.scheduleFit);
        this.observer.observe(this);
        this.observer.observe(this.rail);
      }
      window.addEventListener('resize', this.scheduleFit);
      // Fit synchronously; there is no intermediate frame of partial links.
      // Read fonts.ready afterwards: measuring exposed links can itself start
      // loading their semibold font, replacing an earlier resolved promise.
      this.fit();
      document.fonts?.ready.then(() => {
        if (this.isConnected && this.initialized) this.scheduleFit();
      });
    }

    fit() {
      if (!this.rail || !this.isConnected) return;
      const pinned = this.querySelector(':scope > a');
      if (pinned) {
        pinned.hidden = false;
        const style = getComputedStyle(this);
        const contentWidth = this.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const required = this.summary.getBoundingClientRect().width + pinned.getBoundingClientRect().width + (parseFloat(style.columnGap) || 0);
        if (required > contentWidth) {
          if (document.activeElement === pinned) this.summary.focus({ preventScroll: true });
          pinned.hidden = true;
        }
      }
      const available = this.rail.clientWidth;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      this.links.forEach(link => { link.hidden = false; });
      const widths = this.links.map(link => link.getBoundingClientRect().width);
      let used = 0;
      let count = 0;
      for (const width of widths) {
        const next = used + (count ? gap : 0) + width;
        if (!width || next > available + .1) break;
        used = next;
        count += 1;
      }
      const focused = document.activeElement === document.body ? this.lastFocused : document.activeElement;
      if (this.links.indexOf(focused) >= count) this.summary.focus({ preventScroll: true });
      this.links.forEach((link, index) => { link.hidden = index >= count; });
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onParsed);
      document.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.scheduleFit);
      this.observer?.disconnect();
      cancelAnimationFrame(this.fitFrame);
      this.initialized = false;
    }
  }

  customElements.define('bd-navigation', BDNavigation);
})();
